import React from 'react';
import {
  Box,
  Heading,
  Grid,
  GridItem,
  Flex,
  Skeleton,
  useMediaQuery,
  Text,
  Divider,
} from '@chakra-ui/react';
import DonutChart from './DonutChart';
import LineChart from './LineChart';
import BarChart from './BarChart';
import HeatMap from './HeatMap';
import { useContext } from 'react';
import { ClientDashboardStatsContext } from '../../../store/client/ClientDashboardStatsContext';
import TimeRangeSelector from './TimeRangeSelector';
import NoDataBox from './NoDataBox';

export default function ScansStats({ getData }) {
  const [isLargerThanMd] = useMediaQuery('(min-width: 768px)');
  const dashboardCtx = useContext(ClientDashboardStatsContext);
  const { dashboardState, updateSectionTimeRange, loadingStates } =
    dashboardCtx;
  const {
    scansOverTime,
    heatmap,
    scansPerReaderType,
    scansPerStatus,
    scansPerScanType,
    topReaders,
  } = dashboardState.scanStats;
  const timeRange = dashboardState.scanStats.timeRange;
  const isLoading = loadingStates.scan || loadingStates.global;
  const hasTemplates = dashboardState.totalPassTemplates > 0;
  const hasScansInRange = dashboardState.scanStats.totalScans > 0;

  const SCAN_READER_TYPE_COLORS = {
    passentry: '#ec685a',
    vtap: '#2f3941',
  };

  const SCAN_STATUS_COLORS = {
    accepted: '#ec685a',
    rejected: '#2f3941',
  };

  const SCAN_TYPE_COLORS = {
    nfc: '#2f3941',
    barcode: '#ec685a',
  };

  const formattedTopReaders = Object.entries(topReaders)
    .map(([reader, scans]) => ({
      reader,
      scans,
    }))
    .sort((a, b) => b.scans - a.scans);

  const formatDonutDataReaderType = (scansPerReaderType) => {
    return Object.entries(scansPerReaderType).map(([readerType, value]) => ({
      id: readerType === 'passentry' ? 'PassEntry' : 'VTAP',
      label: readerType === 'passentry' ? 'PassEntry' : 'VTAP',
      value,
      color: SCAN_READER_TYPE_COLORS[readerType],
    }));
  };

  const formatDonutDataStatus = (scansPerStatus) => {
    return Object.entries(scansPerStatus).map(([status, value]) => ({
      id: status.charAt(0).toUpperCase() + status.slice(1),
      label: status.charAt(0).toUpperCase() + status.slice(1),
      value,
      color: SCAN_STATUS_COLORS[status],
    }));
  };

  const formatDonutDataScanType = (scansPerScanType) => {
    return Object.entries(scansPerScanType).map(([scanType, value]) => ({
      id: scanType === 'nfc' ? 'NFC' : 'Barcode',
      label: scanType === 'nfc' ? 'NFC' : 'Barcode',
      value,
      color: SCAN_TYPE_COLORS[scanType],
    }));
  };

  const handleRangeChange = (newRange) => {
    updateSectionTimeRange('scan', newRange);
    getData('scan', newRange);
  };

  return (
    <Box
      bg="white"
      borderRadius="12px"
      boxShadow="0px 5px 10px rgba(0, 0, 0, 0.05)"
      p={7}
      maxW="100%"
      w="100%"
    >
      <Flex
        justify="space-between"
        align={{ base: 'flex-start', md: 'center' }}
        mb={4}
        flexDir={{ base: 'column', md: 'row' }}
        gap={3}
        w="100%"
      >
        <Text textStyle="heading4">Readers & scans</Text>
        {hasTemplates && (
          <TimeRangeSelector
            selectedRange={timeRange}
            onRangeChange={handleRangeChange}
          />
        )}
      </Flex>
      <Divider mb={4} border="1px solid #D9D9D9" />
      {!hasScansInRange && !isLoading ? (
        hasTemplates ? (
          <NoDataBox
            title="No data"
            description="No scan activity in this date range. Try selecting a different range or check back later."
          />
        ) : (
          <NoDataBox
            title="No scan data yet"
            description="Set up a reader and issue passes to start collecting scan activity and usage data."
            buttonText="Set up reader"
            buttonLink="/readers"
            buttonType="outline"
            fullWidth={true}
          />
        )
      ) : (
        <Grid
          templateColumns={{ base: 'repeat(1, 1fr)', xl: 'repeat(2, 1fr)' }}
          gap={8}
          mt={10}
          mb={6}
        >
          <GridItem>
            <Skeleton isLoaded={!isLoading} fadeDuration={1}>
              <Box height="350px" mb={{ base: 4, xl: 0 }}>
                <Heading as="h4" fontSize="16px" mb={4}>
                  Scan Volume Trends
                </Heading>
                <LineChart
                  data={scansOverTime}
                  label="Scans"
                  timeRange={timeRange}
                  tickRotation={isLargerThanMd ? 0 : 45}
                />
              </Box>
            </Skeleton>
          </GridItem>
          <GridItem>
            <Skeleton isLoaded={!isLoading} fadeDuration={1}>
              <Box height="400px" width="100%">
                <Heading as="h4" fontSize="16px" mb={4}>
                  Scan Activity Heatmap
                </Heading>
                <HeatMap data={heatmap} />
              </Box>
            </Skeleton>
          </GridItem>
          <GridItem colSpan={{ base: 1, xl: 2 }}>
            <Grid
              templateColumns={{
                base: 'repeat(1, 1fr)',
                xl: 'repeat(5, 1fr)',
                '2xl': 'repeat(2, 1fr)',
              }}
              gap={8}
              alignItems="center"
            >
              <GridItem colSpan={{ base: 1, xl: 3, '2xl': 1 }}>
                <Grid
                  templateColumns={{
                    base: 'repeat(1, 1fr)',
                    md: 'repeat(3, 1fr)',
                  }}
                  gap={{ base: 2, md: 8 }}
                >
                  <Skeleton isLoaded={!isLoading} fadeDuration={1}>
                    <Box
                      height="300px"
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                    >
                      <Heading
                        as="h4"
                        fontSize="16px"
                        mb={4}
                        textAlign="center"
                      >
                        Scans by Reader Type
                      </Heading>
                      <DonutChart
                        data={formatDonutDataReaderType(scansPerReaderType)}
                        label="Scans per Reader Type"
                        noTotal={true}
                      />
                    </Box>
                  </Skeleton>
                  <Skeleton isLoaded={!isLoading} fadeDuration={1}>
                    <Box
                      height="300px"
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                    >
                      <Heading
                        as="h4"
                        fontSize="16px"
                        mb={4}
                        textAlign="center"
                      >
                        Scans by Status
                      </Heading>
                      <DonutChart
                        data={formatDonutDataStatus(scansPerStatus)}
                        label="Accepted Scans"
                        noTotal={true}
                      />
                    </Box>
                  </Skeleton>
                  <Skeleton isLoaded={!isLoading} fadeDuration={1}>
                    <Box
                      height="300px"
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                    >
                      <Heading
                        as="h4"
                        fontSize="16px"
                        mb={4}
                        textAlign="center"
                      >
                        Scans by Type
                      </Heading>
                      <DonutChart
                        data={formatDonutDataScanType(scansPerScanType)}
                        label="NFC/Barcode Scans"
                        noTotal={true}
                      />
                    </Box>
                  </Skeleton>
                </Grid>
              </GridItem>
              <GridItem colSpan={{ base: 1, xl: 2, '2xl': 1 }}>
                <Skeleton isLoaded={!isLoading} fadeDuration={1}>
                  <Box
                    height="300px"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                  >
                    <Heading as="h4" fontSize="16px" mb={4}>
                      Top readers
                    </Heading>
                    <BarChart data={formattedTopReaders} label="Top Readers" />
                  </Box>
                </Skeleton>
              </GridItem>
            </Grid>
          </GridItem>
        </Grid>
      )}
    </Box>
  );
}
